import * as RP from '@radix-ui/react-popover'
import { Check, ChevronDown, X } from 'lucide-react'
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from './cn'
import { useFieldControl } from './field-context'
import { controlBox } from './Input'
import { floatingPanel, menuItem } from './popover-styles'
import { usePrivateAttrs } from './privateArea'
import { Spinner } from './Spinner'
import { t } from '@/lib/i18n'

export interface ComboboxOption {
  value: string
  label: string
  description?: ReactNode
  icon?: ReactNode
  /** Extra search terms. */
  keywords?: string[]
  disabled?: boolean
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string | null
  onValueChange?: (value: string | null) => void
  placeholder?: string
  emptyText?: string
  /** Called on every keystroke (for server side search). */
  onInputChange?: (query: string) => void
  /** Set false when the options are already filtered by the server. */
  filter?: boolean
  loading?: boolean
  clearable?: boolean
  id?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  'aria-label'?: string
}

function matches(opt: ComboboxOption, q: string) {
  if (!q) return true
  const hay = [opt.label, ...(opt.keywords ?? [])].join(' ').toLowerCase()
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => hay.includes(part))
}

/** Text input with a filtered listbox (ARIA combobox pattern). */
export function Combobox({
  options,
  value = null,
  onValueChange,
  placeholder = t('Search'),
  emptyText = t('No results'),
  onInputChange,
  filter = true,
  loading,
  clearable = true,
  id,
  disabled,
  invalid,
  className,
  'aria-label': ariaLabel,
}: ComboboxProps) {
  const control = useFieldControl({ id, disabled, 'aria-invalid': invalid })
  const listId = `cbx-${useId().replace(/:/g, '')}`
  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const selected = options.find((o) => o.value === value) ?? null
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const text = query ?? selected?.label ?? ''
  const visible = useMemo(
    () => (filter && query ? options.filter((o) => matches(o, query)) : options),
    [filter, options, query],
  )

  const commit = (opt: ComboboxOption | null) => {
    if (opt?.disabled) return
    onValueChange?.(opt ? opt.value : null)
    setQuery(null)
    setOpen(false)
  }
  const move = (delta: number) => {
    if (!visible.length) return
    setOpen(true)
    setActive((i) => (i + delta + visible.length) % visible.length)
  }
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setOpen(true)
      else move(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      move(-1)
    } else if (e.key === 'Home' && open) {
      e.preventDefault()
      setActive(0)
    } else if (e.key === 'End' && open) {
      e.preventDefault()
      setActive(Math.max(0, visible.length - 1))
    } else if (e.key === 'Enter' && open) {
      e.preventDefault()
      if (visible[active]) commit(visible[active])
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        setQuery(null)
      }
    } else if (e.key === 'Tab') {
      setOpen(false)
      setQuery(null)
    }
  }

  // platform-account-4: the input shows the chosen label, and typing after it searched "UTCVancouver". Opening selects
  // that label so typing replaces it, and closing without a choice (Escape too) puts the label back.
  const openList = (input: HTMLInputElement) => {
    setOpen(true)
    if (query !== null) return
    input.select()
    // WebKit on phones puts the caret back after a tap: select once more when the tap is done
    setTimeout(() => {
      if (document.activeElement === input) input.setSelectionRange(0, input.value.length)
    }, 0)
  }
  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery(null)
  }

  const activeId = open && visible[active] ? `${listId}-${active}` : undefined
  if (active > 0 && active >= visible.length) setActive(0)
  const priv = usePrivateAttrs()
  return (
    <RP.Root open={open && !control.disabled} onOpenChange={onOpenChange}>
      <RP.Anchor asChild>
        <div
          ref={anchorRef}
          className={cn(controlBox, 'h-10 pr-1', className)}
          data-invalid={control.invalid || undefined}
          data-disabled={control.disabled || undefined}
        >
          {selected?.icon && query === null && <span className="flex pl-3 text-muted [&_svg]:size-4">{selected.icon}</span>}
          <input
            ref={inputRef}
            id={control.id}
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-invalid={control['aria-invalid']}
            aria-describedby={control['aria-describedby']}
            autoComplete="off"
            disabled={control.disabled}
            placeholder={placeholder}
            value={text}
            onChange={(e) => {
              setQuery(e.target.value)
              setActive(0)
              setOpen(true)
              onInputChange?.(e.target.value)
            }}
            onFocus={(e) => openList(e.currentTarget)}
            onClick={(e) => openList(e.currentTarget)}
            onKeyDown={onKeyDown}
            className="h-full min-w-0 flex-1 bg-transparent px-3 text-text shadow-none outline-none placeholder:text-subtle disabled:cursor-not-allowed"
          />
          {loading && <Spinner size={16} label="" className="mr-2 text-muted" />}
          {clearable && value && !control.disabled ? (
            <button
              type="button"
              aria-label={t('Clear selection')}
              onClick={() => {
                commit(null)
                inputRef.current?.focus()
              }}
              className="inline-flex size-8 items-center justify-center rounded-sm text-muted hover:bg-surface-2 hover:text-text"
            >
              <X className="size-4" strokeWidth={1.75} />
            </button>
          ) : (
            <ChevronDown aria-hidden="true" className="mr-2 size-4 shrink-0 text-muted" strokeWidth={1.75} />
          )}
        </div>
      </RP.Anchor>
      <RP.Portal>
        <RP.Content
          {...priv}
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (anchorRef.current?.contains(e.target as Node)) e.preventDefault()
            else setQuery(null)
          }}
          className={cn(floatingPanel, 'w-(--radix-popover-trigger-width) min-w-[220px] overflow-hidden p-0 data-[state=closed]:animate-none!')}
        >
          <ul id={listId} role="listbox" aria-label={ariaLabel} className="max-h-[300px] overflow-y-auto p-1.5">
            {visible.length === 0 && (
              <li className="px-2.5 py-2 text-body text-muted" role="presentation">
                {loading ? t('Searching') : emptyText}
              </li>
            )}
            {visible.map((opt, i) => (
              <li
                key={opt.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={opt.value === value}
                aria-disabled={opt.disabled || undefined}
                data-highlighted={i === active ? '' : undefined}
                data-disabled={opt.disabled ? '' : undefined}
                onPointerMove={() => setActive(i)}
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => commit(opt)}
                className={cn(menuItem, 'h-auto min-h-9 py-1.5 pr-8')}
              >
                {opt.icon}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{opt.label}</span>
                  {opt.description && <span className="block truncate text-meta text-muted">{opt.description}</span>}
                </span>
                {opt.value === value && <Check className="absolute right-2.5 text-primary!" strokeWidth={2.25} />}
              </li>
            ))}
          </ul>
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  )
}
